import React from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { CircularProgress, IconButton, Typography } from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import MotionCloseIcon from "../../Icons/MotionCloseIcon.svg";
import DialogActions from "@mui/material/DialogActions";
import EMPButton from "../EMPButton/EMPButton";

const EMPDialog = (props) => {
  const {
    open,
    dialogTitle,
    dialogTitleSx,
    dialogHorizontalPadding,
    dialogActionsBottomPadding,
    hasCloseIcon,
    handleClose,
    onEnter,
    onExit,
    children,
    hasSecondaryActionButton,
    secondaryActionButtonText,
    secondaryActionButtonVariant,
    secondaryActionButtonClassName,
    secondaryActionButtonSx,
    hasActionButton,
    actionButtonText,
    actionButtonVariant,
    actionButtonClassName,
    actionButtonSx,
    onConfirm,
    disableActionButton,
    actionLoading,
    onSecondaryAction,
    dialogActionsDataTestId,
    dialogActionsClassName,
    isFord,
    maxWidth,
    fullWidth,
    paperClassName,
    PaperProps,
  } = props;

  return (
    <Dialog
      open={open}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        ...PaperProps,
        className: [PaperProps?.className, paperClassName]
          .filter(Boolean)
          .join(" "),
      }}
      onClose={(event, reason) =>
        reason === "backdropClick" && hasCloseIcon && handleClose?.()
      }
      TransitionProps={{
        mountOnEnter: true,
        unmountOnExit: true,
        onEnter,
        onExit,
      }}
    >
      <DialogTitle
        sx={{
          position: "relative",
          paddingLeft: dialogHorizontalPadding,
          paddingRight: dialogHorizontalPadding,
          ...dialogTitleSx,
        }}
      >
        <Typography
          variant="h4"
          data-testid="dialog-title"
          sx={{
            whiteSpace: "nowrap",
          }}
        >
          {dialogTitle}
        </Typography>

        {hasCloseIcon && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disableRipple
            data-testid="dialog-close-button"
            size="large"
            sx={{
              position: "absolute",
              top: "16px",
              right: "16px",
            }}
          >
            <img src={MotionCloseIcon} alt="close" width="24px" />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent
        sx={{
          paddingLeft: dialogHorizontalPadding,
          paddingRight: dialogHorizontalPadding,
        }}
      >
        {children}
      </DialogContent>

      {(hasActionButton || hasSecondaryActionButton) && (
        <DialogActions
          className={dialogActionsClassName}
          data-testid={dialogActionsDataTestId}
          sx={{
            paddingLeft: dialogHorizontalPadding,
            paddingRight: dialogHorizontalPadding,
            paddingBottom: dialogActionsBottomPadding,
          }}
        >
          {actionLoading && <CircularProgress size={24} />}

          {hasSecondaryActionButton && (
            <EMPButton
              variant={secondaryActionButtonVariant}
              className={secondaryActionButtonClassName}
              isFord={isFord}
              testId="dialog-secondary-action-button"
              onClick={onSecondaryAction}
              sx={secondaryActionButtonSx}
            >
              {secondaryActionButtonText}
            </EMPButton>
          )}

          {hasActionButton && (
            <EMPButton
              variant={actionButtonVariant}
              className={actionButtonClassName}
              isFord={isFord}
              testId="dialog-action-button"
              onClick={onConfirm}
              disabled={disableActionButton}
              sx={actionButtonSx}
            >
              {actionButtonText}
            </EMPButton>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

EMPDialog.propTypes = {
  open: PropTypes.bool,
  dialogTitle: PropTypes.node,
  dialogTitleSx: PropTypes.object,
  dialogHorizontalPadding: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  dialogActionsBottomPadding: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  hasCloseIcon: PropTypes.bool,
  handleClose: PropTypes.func,
  onEnter: PropTypes.func,
  onExit: PropTypes.func,
  children: PropTypes.node,
  hasSecondaryActionButton: PropTypes.bool,
  secondaryActionButtonText: PropTypes.node,
  secondaryActionButtonVariant: PropTypes.string,
  secondaryActionButtonClassName: PropTypes.string,
  secondaryActionButtonSx: PropTypes.object,
  hasActionButton: PropTypes.bool,
  actionButtonText: PropTypes.node,
  actionButtonVariant: PropTypes.string,
  actionButtonClassName: PropTypes.string,
  actionButtonSx: PropTypes.object,
  onConfirm: PropTypes.func,
  disableActionButton: PropTypes.bool,
  actionLoading: PropTypes.bool,
  onSecondaryAction: PropTypes.func,
  dialogActionsDataTestId: PropTypes.string,
  dialogActionsClassName: PropTypes.string,
  isFord: PropTypes.bool,
  maxWidth: PropTypes.oneOfType([
    PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
    PropTypes.bool,
  ]),
  fullWidth: PropTypes.bool,
  paperClassName: PropTypes.string,
  PaperProps: PropTypes.object,
};

EMPDialog.defaultProps = {
  open: false,
  dialogTitle: "",
  dialogTitleSx: {},
  dialogHorizontalPadding: undefined,
  dialogActionsBottomPadding: undefined,
  hasCloseIcon: false,
  handleClose: undefined,
  onEnter: undefined,
  onExit: undefined,
  children: null,
  hasSecondaryActionButton: false,
  secondaryActionButtonText: "",
  secondaryActionButtonVariant: "outlined",
  secondaryActionButtonClassName: undefined,
  secondaryActionButtonSx: {},
  hasActionButton: false,
  actionButtonText: "",
  actionButtonVariant: "contained",
  actionButtonClassName: undefined,
  actionButtonSx: {},
  onConfirm: undefined,
  disableActionButton: false,
  actionLoading: false,
  onSecondaryAction: undefined,
  dialogActionsDataTestId: undefined,
  dialogActionsClassName: undefined,
  isFord: undefined,
  maxWidth: "sm",
  fullWidth: true,
  paperClassName: undefined,
  PaperProps: undefined,
};

export default EMPDialog;
